export type InboundCallTabState = 'accepted' | 'declined' | 'ended';

export type InboundCallTabMessage = {
    readonly callId: string;
    readonly state: InboundCallTabState;
};

export type InboundCallTabChannel = {
    readonly publish: (message: InboundCallTabMessage) => void;
    readonly getResolvedState: (
        callId: string,
    ) => InboundCallTabState | undefined;
    readonly close: () => void;
};

type StoredInboundCallTabMessage = InboundCallTabMessage & {
    readonly sequence: number;
};

type ResolvedInboundCall = {
    readonly state: InboundCallTabState;
    readonly observedAtMs: number;
};

// Twilio can deliver a tab message before the matching SDK invite. Five
// minutes covers that delivery race without retaining call history for the
// full browser session.
const RESOLVED_INBOUND_CALL_TTL_MS = 5 * 60 * 1000;

let storageSequence = 0;

function parseInboundCallTabMessage(
    value: unknown,
): InboundCallTabMessage | null {
    if (
        typeof value !== 'object' ||
        value === null ||
        !('callId' in value) ||
        !('state' in value)
    ) {
        return null;
    }
    const callId = value.callId;
    const state = value.state;
    if (
        typeof callId !== 'string' ||
        (state !== 'accepted' && state !== 'declined' && state !== 'ended')
    ) {
        return null;
    }
    return { callId, state };
}

export function createInboundCallTabChannel({
    organizationId,
    userId,
    onMessage,
}: {
    readonly organizationId: string;
    readonly userId: string;
    readonly onMessage: (message: InboundCallTabMessage) => void;
}): InboundCallTabChannel {
    const scope = `${organizationId}:${userId}`;
    const channelName = `phoebe-inbound-call:${scope}`;
    const storageKey = `phoebe-inbound-call:${scope}:message`;
    const channel =
        typeof BroadcastChannel === 'undefined'
            ? null
            : new BroadcastChannel(channelName);
    const resolvedInboundCalls = new Map<string, ResolvedInboundCall>();

    function pruneResolvedInboundCalls(nowMs: number): void {
        for (const [callId, resolvedCall] of resolvedInboundCalls) {
            if (
                nowMs - resolvedCall.observedAtMs >
                RESOLVED_INBOUND_CALL_TTL_MS
            ) {
                resolvedInboundCalls.delete(callId);
            }
        }
    }

    function rememberResolvedInboundCall(message: InboundCallTabMessage): void {
        const nowMs = Date.now();
        pruneResolvedInboundCalls(nowMs);
        resolvedInboundCalls.set(message.callId, {
            state: message.state,
            observedAtMs: nowMs,
        });
    }

    function receive(message: InboundCallTabMessage): void {
        rememberResolvedInboundCall(message);
        onMessage(message);
    }

    if (channel) {
        channel.onmessage = (event: MessageEvent<unknown>) => {
            const message = parseInboundCallTabMessage(event.data);
            if (message) {
                receive(message);
            }
        };
    }

    const storageHandler = (event: StorageEvent) => {
        if (event.key !== storageKey || event.newValue === null) {
            return;
        }
        try {
            const message = parseInboundCallTabMessage(
                JSON.parse(event.newValue),
            );
            if (message) {
                receive(message);
            }
        } catch {
            // Ignore malformed cross-tab state written outside this module.
        }
    };
    window.addEventListener('storage', storageHandler);

    return {
        publish(message) {
            rememberResolvedInboundCall(message);
            const storedMessage: StoredInboundCallTabMessage = {
                ...message,
                sequence: ++storageSequence,
            };
            if (channel) {
                channel.postMessage(storedMessage);
                return;
            }
            try {
                localStorage.setItem(storageKey, JSON.stringify(storedMessage));
            } catch {
                // Some browser privacy settings block local storage.
            }
        },
        getResolvedState(callId) {
            pruneResolvedInboundCalls(Date.now());
            return resolvedInboundCalls.get(callId)?.state;
        },
        close() {
            resolvedInboundCalls.clear();
            channel?.close();
            window.removeEventListener('storage', storageHandler);
        },
    };
}
